import { type Dispatch, memo, type SetStateAction, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import {
  type ActiveBoostItemProps,
  type BoostCardProps,
} from '@common/types/components';
import { CoinBalanceTopUp } from '@components/CoinBalanceTopUp/CoinBalanceTopUp';
import { H3, StringMain } from '@components/Typography/Typography';
import cn from 'classnames';

import { type PlantType } from '@/common/types/common';
import { Close } from '@/components/ui/Close/Close';

import css from './styles.module.scss';

interface PlantBoostModalProps<
  T extends ActiveBoostItemProps,
  R extends BoostCardProps,
> {
  activeBoostList: T[];
  activeBoostItem: (props: T) => JSX.Element | null;
  boostCardList: R[];
  boostCardItem: (props: R) => JSX.Element | null;
  setOpenPlantBoostModal: Dispatch<SetStateAction<boolean>>;
  isPlantBoostOpen: boolean;
  plantType: PlantType;
}

const PlantBoostModal = memo(
  <T extends ActiveBoostItemProps, R extends BoostCardProps>({
    activeBoostList,
    activeBoostItem,
    boostCardList,
    boostCardItem,
    setOpenPlantBoostModal,
    isPlantBoostOpen,
    plantType,
  }: PlantBoostModalProps<T, R>): JSX.Element => {
    const nodeRef = useRef(null);
    const { t } = useTranslation();

    const getBoostDescription = (plantType: PlantType): string => {
      switch (plantType) {
        case 'Quarry': {
          return t('All Extractors');
        }
        case 'Factory': {
          return t('All Plant');
        }
        case 'Workshop': {
          return t('All Factory');
        }
        default: {
          return '';
        }
      }
    };

    return (
      <div
        className={`modalUpdateBackground modalTcoin ${isPlantBoostOpen ? 'open' : ''}`}
        style={{ background: 'transparent' }}
      >
        <CSSTransition
          nodeRef={nodeRef}
          in={isPlantBoostOpen}
          timeout={300}
          classNames="my-node-modal"
          unmountOnExit
        >
          <div
            ref={nodeRef}
            id="update-modal"
            className={cn(
              'page page--mine modalUpdate !pt-4 bg-main-bg overflow-hidden compositing-layer-hook',
            )}
            style={{
              padding: '2rem 1rem 2.5rem 1rem',
              bottom: 0,
              height: 'auto',
              boxShadow:
                '0px -32px 40px 0px #181A20, 0px 1px 0px 0px rgba(255, 255, 255, 0.20) inset',
              // bottom: !location.pathname.includes('production') ? '-15%' : '-34%',
            }}
          >
            <div className={cn('update-modal', css.gradient)}>
              <div className="flex items-center justify-between gap-2">
                <H3 weight="bold">{t('Boost list')}</H3>
                <CoinBalanceTopUp
                  className="!h-12 !pl-2 !pr-3 py-[0.625rem] ml-auto z-[3] compositing-layer-hook"
                  contenctClassName="!items-start"
                  compare={Infinity}
                  iconSize="1.75rem"
                />
                <Close
                  onClick={() => setOpenPlantBoostModal(false)}
                  imageVariant="modal"
                  variant="modal"
                />
              </div>
              <ul className="plant_boost_list">
                {activeBoostList.map((props) => activeBoostItem(props))}
              </ul>
              <StringMain weight="medium" className="block mb-2 mt-6">
                {getBoostDescription(plantType)}
              </StringMain>
              <div className="gap-2 !grid grid-cols-2">
                {boostCardList.map((props) => boostCardItem(props))}
              </div>
            </div>
          </div>
        </CSSTransition>
      </div>
    );
  },
);

export default PlantBoostModal;
